import React, { useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { fmtEUR, fmtPct } from "../../lib/format";
import { Card } from "../ui/card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/table";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";

export const Semaforo = ({ v }) => {
  const cls = { ok: "bg-emerald-500", attenzione: "bg-amber-500", critico: "bg-red-500" }[v] || "bg-slate-300";
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${cls}`} title={v} data-testid={`semaforo-${v}`} />;
};

export const Delta = ({ v, pct, invert }) => {
  const bad = invert ? v < 0 : v > 0;
  const cls = Math.abs(v) < 0.005 ? "text-muted-foreground" : bad ? "text-destructive" : "text-emerald-600";
  return <span className={`font-medium ${cls}`}>{v > 0 ? "+" : ""}{fmtEUR(v)}{pct != null && !isNaN(pct) ? <span className="text-xs ml-1">({pct > 0 ? "+" : ""}{fmtPct(pct)})</span> : null}</span>;
};

const short = (v) => new Intl.NumberFormat("it-IT", { notation: "compact", maximumFractionDigits: 1 }).format(v);

export default function VarianceTab({ operationId }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/operations/${operationId}/variance`).then((r) => setD(r.data)).catch((e) => toast.error(fmtErr(e.response?.data?.detail))); }, [operationId]);
  if (!d) return <div className="text-muted-foreground">Caricamento...</div>;

  const box = (label, o, invert) => (
    <Card className="p-4" data-testid={`variance-${label.toLowerCase()}`}>
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
        <div><div className="text-xs text-muted-foreground">Previsto</div><div className="font-semibold kpi-num">{fmtEUR(o.previsto)}</div></div>
        <div><div className="text-xs text-muted-foreground">Effettivo</div><div className="font-semibold kpi-num">{fmtEUR(o.effettivo)}</div></div>
      </div>
      <div className="mt-2 text-xs text-muted-foreground">Scostamento</div>
      <Delta v={o.scostamento} pct={o.scostamento_pct} invert={invert} />
    </Card>
  );

  return (
    <div className="space-y-5" data-testid="variance-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{box("Costi", d.costi, false)}{box("Ricavi", d.ricavi, true)}{box("Margine", d.margine, true)}</div>
      <Card className="p-4">
        <div className="text-sm font-semibold mb-2">Previsto vs effettivo per voce di costo</div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={d.voci.map((v) => ({ name: v.label, Previsto: v.previsto, Effettivo: v.effettivo, Impegnato: v.impegnato }))}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e0" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={60} />
            <YAxis tickFormatter={short} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => fmtEUR(v)} />
            <Legend />
            <Bar dataKey="Previsto" fill="#1e3a5f" radius={[3, 3, 0, 0]} />
            <Bar dataKey="Effettivo" fill="#2b9bd4" radius={[3, 3, 0, 0]} />
            <Bar dataKey="Impegnato" fill="#f59e0b" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>
      <Card className="p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-muted/50"><TableHead></TableHead><TableHead>Voce</TableHead><TableHead className="text-right">Previsto</TableHead><TableHead className="text-right">Effettivo</TableHead><TableHead className="text-right">Impegnato</TableHead><TableHead className="text-right">Proiezione</TableHead><TableHead className="text-right">Scostamento</TableHead></TableRow></TableHeader>
          <TableBody>
            {d.voci.map((v) => (
              <TableRow key={v.voce} data-testid="variance-row">
                <TableCell><Semaforo v={v.semaforo} /></TableCell>
                <TableCell className="font-medium">{v.label}</TableCell>
                <TableCell className="text-right">{fmtEUR(v.previsto)}</TableCell>
                <TableCell className="text-right">{fmtEUR(v.effettivo)}</TableCell>
                <TableCell className="text-right text-amber-600">{fmtEUR(v.impegnato)}</TableCell>
                <TableCell className="text-right">{fmtEUR(v.proiezione)}</TableCell>
                <TableCell className="text-right"><Delta v={v.scostamento} pct={v.scostamento_pct} /></TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-muted/40 font-semibold"><TableCell></TableCell><TableCell>Totale costi</TableCell><TableCell className="text-right">{fmtEUR(d.costi.previsto)}</TableCell><TableCell className="text-right">{fmtEUR(d.costi.effettivo)}</TableCell><TableCell></TableCell><TableCell></TableCell><TableCell className="text-right"><Delta v={d.costi.scostamento} pct={d.costi.scostamento_pct} /></TableCell></TableRow>
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
